import { getData, postData, putData, deleteData } from '@/utils/api';
import { ApiResponse, RequestOptions } from '@/types/api';
import {
  Status,
  TeamMemberSortConfig,
  TeamNotesSortConfig,
  TeamApiModel,
  CustomerPortalApiModel,
  TeamDetailApiModel,
  TeamCreateDataModel,
  TeamMembersPagingApiModel,
  TeamMemberApiModel,
  TeamMemberLightApiModel,
  TeamMembersApiModel,
  TeamMemberCategoryUpdateDataModel,
  SupportersWithTeamMemberApiModel,
  TeamCategoriesApiModel,
  TeamCategoryUpdateDataModel,
  TeamMemberRoleUpdateDataModel,
  TeamMemberStatusUpdateDataModel
} from '@/types/teams';
import {
  FootballlogApiModel,
  FootballlogFeedListApiModel,
  TeamMembersFootballlogFeedListApiModel,
  TeamMembersFootballlogListApiPagingModel
} from '@/types/footballlogs';
import { TeamMembersGoalsPagingApiModel } from '@/types/goals';

/**
 * チーム情報を取得
 */
export async function getTeamByUUID(
  teamUUID: string,
  options?: RequestOptions
): Promise<ApiResponse<TeamDetailApiModel>> {
  const apiPath = `teams/${teamUUID}/`;

  return await getData<TeamDetailApiModel>(apiPath, options);
}

/**
 * カスタマーポータルURLを取得
 */
export async function getCustomerPortalUrl(
  teamUUID: string,
  options?: RequestOptions
): Promise<ApiResponse<CustomerPortalApiModel>> {
  const apiPath = `teams/${teamUUID}/customer-portal/`;

  return await getData<CustomerPortalApiModel>(apiPath, options);
}

/**
 * 新しいのチームの作成
 */
export async function createTeam(
  formData: TeamCreateDataModel,
  csrftoken: string,
  options?: RequestOptions
): Promise<ApiResponse<TeamApiModel>> {
  const apiPath = `teams/`;

  return await postData<TeamApiModel>(apiPath, csrftoken, formData, options);
}

/**
 * チーム情報の更新
 */
export async function updateTeam(
  teamUUID: string,
  formData: FormData,
  csrftoken: string,
  options?: RequestOptions
): Promise<ApiResponse<void>> {
  const apiPath = `teams/${teamUUID}/`;
  return await putData<void>(apiPath, csrftoken, formData, options);
}

/**
 * チームの削除
 */
export async function deleteTeam(
  teamUUID: string,
  csrftoken: string,
  options?: RequestOptions
): Promise<ApiResponse<void>> {
  const apiPath = `teams/${teamUUID}/`;

  return await deleteData<void>(apiPath, csrftoken, options);
}

/**
 * 所属メンバーを取得
 * NOTE: 現状チームメンバーのステータスしか参照しないのでLightModelを使用している。
 */
export async function getTeamMember(
  teamUUID: string,
  userId: number,
  options?: RequestOptions
): Promise<ApiResponse<TeamMemberLightApiModel>> {
  const params = new URLSearchParams();
  params.set('teamuuid', teamUUID);
  params.set('userid', String(userId));
  const queryParams = `?${params.toString()}`;
  const apiPath = `team-members/member/${queryParams}`;

  return await getData<TeamMemberLightApiModel>(apiPath, options);
}

/**
 * 招待メンバー一覧を取得
 * NOTE: status=1(チーム招待中)
 */
export async function getTeamMemberInvitations(
  teamUUID: string,
  limit: number,
  offset: number,
  options?: RequestOptions
): Promise<ApiResponse<TeamMembersApiModel>> {
  const params = new URLSearchParams();

  params.set('teamuuid', teamUUID);
  // メンバー、招待中のみ
  params.append('status', `${String(Status.Requested)}`);

  // デフォルトソート(固定): 招待順にソート
  params.append('sort', 'updatedat');
  params.append('order', 'desc');

  params.set('limit', String(limit));
  params.set('offset', String(offset));

  const queryParams = `?${params.toString()}`;
  const apiPath = `team-members/${queryParams}`;

  return await getData<TeamMembersApiModel>(apiPath, options);
}

/**
 * 所属メンバー一覧を取得
 * NOTE: status=2(チーム所属)、status=3(チーム所属OBOG)
 */
export async function getTeamMembers(
  teamUUID: string,
  limit: number,
  offset: number,
  sorts?: TeamMemberSortConfig | TeamMemberSortConfig[],
  options?: RequestOptions
): Promise<ApiResponse<TeamMembersApiModel>> {
  const params = new URLSearchParams();

  params.set('teamuuid', teamUUID);
  // メンバー、OGOBのみ
  params.append('status', `${String(Status.Approved)},${String(Status.OBOG)}`);

  // ソート指定時、単一のsortオブジェクトの場合と配列の場合を処理
  if (sorts) {
    const sortArray = Array.isArray(sorts) ? sorts : [sorts];
    sortArray.forEach((sort) => {
      params.append('sort', sort.field);
      params.append('order', sort.order);
    });
  }

  params.set('limit', String(limit));
  params.set('offset', String(offset));

  const queryParams = `?${params.toString()}`;
  const apiPath = `team-members/${queryParams}`;

  return await getData<TeamMembersApiModel>(apiPath, options);
}

/**
 * チームメンバー一覧を取得(ページング形式)
 * NOTE: バックエンドで、status=2,3(チーム所属、OBOG済み)以外のレコードは参照できない
 * ように制御されている。
 */
export async function getTeamMembersPaging(
  teamUUID: string,
  pageNumber?: number,
  categories?: Set<number>,
  statuses?: Set<number>,
  sorts?: TeamMemberSortConfig | TeamMemberSortConfig[],
  options?: RequestOptions
): Promise<ApiResponse<TeamMembersPagingApiModel>> {
  const params = new URLSearchParams();

  params.set('teamuuid', teamUUID);

  if (pageNumber) {
    params.set('page', String(pageNumber));
  }

  // 絞り込み: カテゴリー
  if (categories && categories.size > 0) {
    params.set('categories', Array.from(categories).join(','));
  }

  //絞り込み: sutatus(メンバー、OGOBの絞り込み。それ以外は不可)
  if (statuses && statuses.size > 0) {
    params.set('status', Array.from(statuses).join(','));
  }

  // 単一のsortオブジェクトの場合と配列の場合を処理
  if (sorts) {
    const sortArray = Array.isArray(sorts) ? sorts : [sorts];
    sortArray.forEach((sort) => {
      params.append('sort', sort.field);
      params.append('order', sort.order);
    });
  }

  const queryParams = `?${params.toString()}`;
  const apiPath = `team-members/paging/${queryParams}`;

  return await getData<TeamMembersPagingApiModel>(apiPath, options);
}

/**
 * チーム招待
 */
export async function inviteTeam(
  teamUUID: string,
  footballerId: number,
  csrftoken: string,
  options?: RequestOptions
): Promise<ApiResponse<TeamMemberLightApiModel>> {
  const queryParams = `?teamuuid=${teamUUID}`;
  const apiPath = `team-members/${footballerId}/invite-team/${queryParams}`;

  return await putData<TeamMemberLightApiModel>(apiPath, csrftoken, undefined, options);
}

/**
 * チーム招待の承認
 */
export async function approveInvitation(
  teamUUID: string,
  footballerId: number,
  csrftoken: string,
  options?: RequestOptions
): Promise<ApiResponse<TeamMemberApiModel>> {
  const queryParams = `?teamuuid=${teamUUID}`;
  const apiPath = `team-members/${footballerId}/approve-invitation/${queryParams}`;

  return await putData<TeamMemberApiModel>(apiPath, csrftoken, undefined, options);
}

/**
 * チーム招待のキャンセル
 */
export async function cancelInvitation(
  teamUUID: string,
  footballerId: number,
  csrftoken: string,
  options?: RequestOptions
): Promise<ApiResponse<void>> {
  const queryParams = `?teamuuid=${teamUUID}`;
  const apiPath = `team-members/${footballerId}/cancel-invitation/${queryParams}`;

  return await putData<void>(apiPath, csrftoken, undefined, options);
}

/**
 * チームの脱退
 */
export async function leaveTeam(
  teamUUID: string,
  footballerId: number,
  csrftoken: string,
  options?: RequestOptions
): Promise<ApiResponse<void>> {
  const queryParams = `?teamuuid=${teamUUID}`;
  const apiPath = `team-members/${footballerId}/leave-team/${queryParams}`;

  return await putData<void>(apiPath, csrftoken, undefined, options);
}

/**
 * チームメンバーのステータス更新
 * NOTE: OBOG（status=3） <-> 一般メンバー（status=2）の切り替えに使用する
 */
export async function updateTeamMemberStatus(
  teamUUID: string,
  formData: TeamMemberStatusUpdateDataModel,
  csrftoken: string,
  options?: RequestOptions
): Promise<ApiResponse<TeamMemberApiModel>> {
  const queryParams = `?teamuuid=${teamUUID}`;
  const apiPath = `team-members/${formData.team_member_id}/update-status/${queryParams}`;

  return await postData<TeamMemberApiModel>(apiPath, csrftoken, formData, options);
}

/**
 * チームメンバーのカテゴリー更新
 */
export async function updateTeamMemberCategory(
  teamUUID: string,
  formData: TeamMemberCategoryUpdateDataModel,
  csrftoken: string,
  options?: RequestOptions
): Promise<ApiResponse<TeamMemberApiModel>> {
  const queryParams = `?teamuuid=${teamUUID}`;
  const apiPath = `team-members/${formData.team_member_id}/update-category/${queryParams}`;

  return await postData<TeamMemberApiModel>(apiPath, csrftoken, formData, options);
}

/**
 * チームメンバーのロール更新
 */
export async function updateTeamMemberRole(
  teamUUID: string,
  formData: TeamMemberRoleUpdateDataModel,
  csrftoken: string,
  options?: RequestOptions
): Promise<ApiResponse<TeamMemberApiModel>> {
  const queryParams = `?teamuuid=${teamUUID}`;
  const apiPath = `team-members/${formData.team_member_id}/update-role/${queryParams}`;

  return await postData<TeamMemberApiModel>(apiPath, csrftoken, formData, options);
}

/**
 * サポーター情報とチームメンバー情報を併せて取得
 */
export async function getSupportersWithTeamMember(
  teamUUID: string,
  limit: number,
  offset: number,
  options?: RequestOptions
): Promise<ApiResponse<SupportersWithTeamMemberApiModel>> {
  const apiPath = `supporters/?limit=${limit}&offset=${offset}&teamuuid=${teamUUID}`;

  return await getData<SupportersWithTeamMemberApiModel>(apiPath, options);
}

/**
 * チームカテゴリー一覧を取得
 */
export async function getTeamCategories(
  teamUUID: string,
  options?: RequestOptions
): Promise<ApiResponse<TeamCategoriesApiModel>> {
  const queryParams = `?teamuuid=${teamUUID}`;

  const apiPath = `team-categories/${queryParams}`;

  return await getData<TeamCategoriesApiModel>(apiPath, options);
}

/**
 * チームカテゴリーの更新
 */
export async function updateTeamCategory(
  teamUUID: string,
  formData: TeamCategoryUpdateDataModel,
  csrftoken: string,
  options?: RequestOptions
): Promise<ApiResponse<void>> {
  const queryParams = `?teamuuid=${teamUUID}`;
  const apiPath = `team-categories/${formData.id}/${queryParams}`;

  return await putData<void>(apiPath, csrftoken, formData, options);
}

/**
 * チームサッカーノート一覧を取得(ページング形式)
 */
export async function getTeamMembersFootballlogsPaging(
  teamUUID: string,
  pageNumber?: number,
  sorts?: TeamNotesSortConfig | TeamNotesSortConfig[],
  categories?: Set<number>,
  options?: RequestOptions
): Promise<ApiResponse<TeamMembersFootballlogListApiPagingModel>> {
  const params = new URLSearchParams();

  if (pageNumber) {
    params.set('page', String(pageNumber));
  }

  // 単一のsortオブジェクトの場合と配列の場合を処理
  if (sorts) {
    const sortArray = Array.isArray(sorts) ? sorts : [sorts];
    sortArray.forEach((sort) => {
      params.append('sort', sort.field);
      params.append('order', sort.order);
    });
  }

  if (categories && categories.size > 0) {
    params.set('categories', Array.from(categories).join(','));
  }

  const queryParams = `?${params.toString()}`;
  const apiPath = `teams/${teamUUID}/footballlogs/paging/${queryParams}`;

  return await getData<TeamMembersFootballlogListApiPagingModel>(apiPath, options);
}

/**
 *  対象チームのサッカーノートFeedsを取得
 */
export async function getTeamFootballlogFeeds(
  teamUUID: string,
  limit: number,
  offset: number,
  options?: RequestOptions
): Promise<ApiResponse<TeamMembersFootballlogFeedListApiModel>> {
  const apiPath = `teams/${teamUUID}/footballlogs/?limit=${limit}&offset=${offset}`;

  return await getData<TeamMembersFootballlogFeedListApiModel>(apiPath, options);
}

/**
 *  対象チームメンバー（1人）のサッカーノートFeedsを取得
 */
export async function getTeamMemberFootballlogFeeds(
  teamUUID: string,
  footballerId: number,
  limit: number,
  offset: number,
  options?: RequestOptions
): Promise<ApiResponse<FootballlogFeedListApiModel>> {
  const apiPath = `teams/${teamUUID}/footballers/${footballerId}/footballlogs/?limit=${limit}&offset=${offset}`;

  return await getData<FootballlogFeedListApiModel>(apiPath, options);
}

/**
 * 特定のチームノートを取得
 */
export async function getTeamFootballlog(
  teamUUID: string,
  footballlogId: number,
  options?: RequestOptions
): Promise<ApiResponse<FootballlogApiModel>> {
  const apiPath = `teams/${teamUUID}/footballlogs/${footballlogId}/`;

  return await getData<FootballlogApiModel>(apiPath, options);
}

/**
 * チームメンバーの最新のチームノートを取得
 */
export async function getTeamMembersCurrentFootballlog(
  teamUUID: string,
  footballerId: number,
  options?: RequestOptions
): Promise<ApiResponse<FootballlogApiModel>> {
  const apiPath = `teams/${teamUUID}/footballers/${footballerId}/footballlogs/current/`;

  return await getData<FootballlogApiModel>(apiPath, options);
}

/**
 * チームメンバーの現在掲げている目標一覧を取得(ページング形式)
 */
export async function getTeamMembersGoalsPaging(
  teamUUID: string,
  pageNumber?: number,
  sorts?: TeamNotesSortConfig | TeamNotesSortConfig[],
  categories?: Set<number>,
  options?: RequestOptions
): Promise<ApiResponse<TeamMembersGoalsPagingApiModel>> {
  const params = new URLSearchParams();

  if (pageNumber) {
    params.set('page', String(pageNumber));
  }

  // 単一のsortオブジェクトの場合と配列の場合を処理
  if (sorts) {
    const sortArray = Array.isArray(sorts) ? sorts : [sorts];
    sortArray.forEach((sort) => {
      params.append('sort', sort.field);
      params.append('order', sort.order);
    });
  }

  if (categories && categories.size > 0) {
    params.set('categories', Array.from(categories).join(','));
  }

  const queryParams = `?${params.toString()}`;
  const apiPath = `teams/${teamUUID}/goals/paging/${queryParams}`;

  return await getData<TeamMembersGoalsPagingApiModel>(apiPath, options);
}
